import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AddressAutocomplete from '../../../components/ui/AddressAutocomplete';
import Alert from '../../../components/ui/Alert';
import Button from '../../../components/ui/Button';
import Card, { CardContent, CardFooter, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import Skeleton from '../../../components/ui/Skeleton';
import { usePageTitle } from '../../../hooks/shared';
import { MAILER_PRICE, MAILER_TRIGGERS } from '../../../lib/mailer-triggers';
import { civicAPI } from '../../elections/api/civic';
import { lookupAddress } from '../../elections/api/lookup';
import { addressesAPI } from '../../identity/api/addresses';
import { useAuthStore } from '../../identity/store/authStore';
import { useCreateMailerOrder, useMailerPreview } from '../hooks/useMailerOrders';

function RecipientCard({ recipient, index, onUpdate, onRemove, showRemove }) {
  const handleTriggerChange = (triggerKey, checked) => {
    const newTriggers = checked
      ? [...recipient.triggers, triggerKey]
      : recipient.triggers.filter((t) => t !== triggerKey);
    onUpdate(index, { ...recipient, triggers: newTriggers });
  };

  const handleFieldChange = (field, value) => {
    onUpdate(index, { ...recipient, [field]: value });
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-base">
          {recipient.isSponsored ? 'Sponsor Mailers' : 'Your Mailers'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor={`name-${index}`}>
            Recipient Name{' '}
            <span className="font-normal text-neutral-500 normal-case tracking-normal">
              (optional)
            </span>
          </Label>
          <Input
            id={`name-${index}`}
            placeholder="Full name"
            value={recipient.recipientName}
            onChange={(e) => handleFieldChange('recipientName', e.target.value)}
            autoComplete="name"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor={`address-${index}`}>Mailing Address</Label>
          <AddressAutocomplete
            id={`address-${index}`}
            value={recipient.mailingAddress}
            onChange={(val) => handleFieldChange('mailingAddress', val)}
            onSelect={(val) => handleFieldChange('mailingAddress', val)}
            placeholder="Start typing your address…"
            className="mt-1"
          />
        </div>

        <div>
          <Label className="text-sm font-semibold">Select mailer triggers:</Label>
          <div className="space-y-2 mt-3">
            {Object.entries(MAILER_TRIGGERS).map(([triggerKey, trigger]) => (
              <div key={triggerKey} className="flex items-center gap-2">
                <Checkbox
                  id={`trigger-${index}-${triggerKey}`}
                  checked={recipient.triggers.includes(triggerKey)}
                  onCheckedChange={(checked) => handleTriggerChange(triggerKey, checked)}
                />
                <Label
                  htmlFor={`trigger-${index}-${triggerKey}`}
                  className="cursor-pointer text-sm flex-1"
                >
                  {trigger.label} — ${MAILER_PRICE.toFixed(2)}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-neutral-200">
          <p className="text-sm">
            <span className="font-semibold">
              ${(recipient.triggers.length * MAILER_PRICE).toFixed(2)}
            </span>{' '}
            for this address
          </p>
        </div>
      </CardContent>

      {showRemove && (
        <CardFooter>
          <Button variant="destructive" size="sm" onClick={() => onRemove(index)}>
            Remove recipient
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}

RecipientCard.propTypes = {
  recipient: PropTypes.shape({
    recipientName: PropTypes.string.isRequired,
    mailingAddress: PropTypes.string.isRequired,
    isSponsored: PropTypes.bool,
    triggers: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  showRemove: PropTypes.bool,
};

RecipientCard.defaultProps = {
  showRemove: true,
};

export default function MailerSetupPage() {
  usePageTitle('Set Up Mailers');

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [searchParams] = useSearchParams();
  const addressFromUrl = searchParams.get('address') || '';

  const [votingAddress, setVotingAddress] = useState(addressFromUrl);
  const [selectedElectionIds, setSelectedElectionIds] = useState([]);
  // The backend currently bills one order per election. We submit using the
  // first selected election; the UI warns if the user picks more than one.
  const electionId = selectedElectionIds[0] || '';
  const [recipients, setRecipients] = useState([
    { recipientName: '', mailingAddress: addressFromUrl, isSponsored: false, triggers: [] },
  ]);
  const [donationAmount, setDonationAmount] = useState(0);

  const recipientReducer = (state, action) => {
    switch (action.type) {
      case 'UPDATE':
        return state.map((r, i) => (i === action.index ? action.payload : r));
      case 'REMOVE':
        return state.filter((_, i) => i !== action.index);
      case 'ADD':
        return [
          ...state,
          { recipientName: '', mailingAddress: '', isSponsored: true, triggers: [] },
        ];
      default:
        return state;
    }
  };

  const [recipientState, dispatch] = useReducer(recipientReducer, recipients);

  useEffect(() => {
    setRecipients(recipientState);
  }, [recipientState]);

  // Keep votingAddress in sync with the first recipient's mailing address so
  // that editing it on the form updates the elections list. Debounced ~500ms
  // so neither the geocode lookup nor the elections query re-runs on every
  // keystroke.
  const primaryMailingAddress = recipientState[0]?.mailingAddress || '';
  useEffect(() => {
    if (!primaryMailingAddress) return undefined;
    const handle = setTimeout(() => {
      setVotingAddress((prev) => (prev === primaryMailingAddress ? prev : primaryMailingAddress));
    }, 500);
    return () => clearTimeout(handle);
  }, [primaryMailingAddress]);

  // When the voter switches addresses, drop any election picks from the old
  // list so the default-select effect re-picks the closest upcoming one.
  useEffect(() => {
    setSelectedElectionIds([]);
  }, [votingAddress]);

  // For logged-in users arriving without a URL address, load their primary saved address
  const { data: savedAddressData } = useQuery({
    queryKey: ['addresses'],
    queryFn: addressesAPI.listAddresses,
    enabled: isAuthenticated && !addressFromUrl,
  });

  useEffect(() => {
    if (!savedAddressData?.addresses?.length || addressFromUrl) return;
    const primary =
      savedAddressData.addresses.find((a) => a.isPrimary) || savedAddressData.addresses[0];
    const formatted = [primary.streetAddress, primary.city, primary.state, primary.zipCode]
      .filter(Boolean)
      .join(', ');
    setVotingAddress(formatted);
    dispatch({
      type: 'UPDATE',
      index: 0,
      payload: { ...recipientState[0], mailingAddress: formatted },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddressData]);

  // Geocode the voting address — we use this to extract the state for the
  // elections query below. (We don't rely on the lookup's `election` field
  // because it's scoped to TARGET_STATE/TARGET_COUNTY and returns only one.)
  const { data: lookupResult, isFetching: isLookingUp } = useQuery({
    queryKey: ['address-lookup-election', votingAddress],
    queryFn: () => lookupAddress(votingAddress),
    enabled: votingAddress.length >= 10,
    staleTime: 5 * 60 * 1000,
  });

  // Extract a 2-letter state code straight from the address string as a
  // fallback. The Census geocoder chokes on addresses with trailing ", USA"
  // or unusual road names (e.g. "Colorado 91, Leadville, CO, USA"), so we
  // can't rely on the backend always returning a geocoded state.
  const stateFromAddress = useMemo(() => {
    if (!votingAddress) return null;
    const m = votingAddress.match(/,\s*([A-Z]{2})(?=[\s,]|$)/);
    return m ? m[1] : null;
  }, [votingAddress]);

  const resolvedState =
    lookupResult?.geocoded?.state || lookupResult?.state || stateFromAddress || null;

  // Pull every upcoming election in that state — same source the search results
  // page uses, so what the user saw there will show up here too.
  const {
    data: electionsData,
    isFetching: isLoadingElections,
    isError: electionsFailed,
  } = useQuery({
    queryKey: ['upcoming-elections', resolvedState],
    queryFn: () => civicAPI.getElections({ state: resolvedState }),
    enabled: Boolean(resolvedState),
    staleTime: 5 * 60 * 1000,
  });

  const upcomingElections = useMemo(() => electionsData?.elections || [], [electionsData]);

  // Default-select the closest upcoming election when the list first loads.
  useEffect(() => {
    if (selectedElectionIds.length === 0 && upcomingElections.length > 0) {
      setSelectedElectionIds([upcomingElections[0].id]);
    }
  }, [upcomingElections, selectedElectionIds.length]);

  const toggleElection = (id) => {
    setSelectedElectionIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const { data: previewData } = useMailerPreview({ recipients, donationAmount, electionId });
  const {
    createOrder,
    isLoading: isCreating,
    isRedirecting,
    error: createError,
  } = useCreateMailerOrder();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!electionId) {
      alert(
        upcomingElections.length === 0 && votingAddress
          ? 'No upcoming elections were found for your address. Check back closer to election season.'
          : 'Pick the election you want a mailer for.'
      );
      return;
    }

    const hasValidRecipients = recipients.every((r) => r.mailingAddress && r.triggers.length > 0);

    if (!hasValidRecipients) {
      alert('Each recipient needs a mailing address and at least one mailer date.');
      return;
    }

    createOrder({ electionId, recipients, donationAmount });
  };

  const subtotal = previewData?.subtotal || 0;
  const roundUpAmount = Math.ceil(subtotal) - subtotal;
  const suggestedTotal = subtotal + (donationAmount || roundUpAmount);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-2">Set Up Your Mailers</h1>
      <p className="text-neutral-600 mb-8">
        Choose when you&apos;d like to receive ballot guides during the election cycle
      </p>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {createError && <Alert variant="destructive">Error: {createError.message}</Alert>}

          {/* Election picker — every upcoming election at the voter's address */}
          <Card>
            <CardHeader>
              <CardTitle>Pick an election</CardTitle>
            </CardHeader>
            <CardContent>
              {!votingAddress && (
                <p className="text-sm text-neutral-500">
                  Enter your address below and we&apos;ll list the upcoming elections you can vote
                  in.
                </p>
              )}

              {votingAddress && (isLookingUp || isLoadingElections) && (
                <div className="space-y-2">
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="h-5 w-1/2" />
                  <Skeleton className="h-5 w-3/4" />
                </div>
              )}

              {votingAddress &&
                !isLookingUp &&
                !isLoadingElections &&
                upcomingElections.length > 0 && (
                  <div className="space-y-2">
                    {upcomingElections.map((e) => {
                      const checked = selectedElectionIds.includes(e.id);
                      return (
                        // eslint-disable-next-line jsx-a11y/label-has-associated-control -- htmlFor/id pair plus visible text in children
                        <label
                          key={e.id}
                          htmlFor={`election-${e.id}`}
                          className={`flex items-start gap-3 p-3 border-2 cursor-pointer transition-colors ${
                            checked
                              ? 'border-primary-500 bg-success-100'
                              : 'border-neutral-200 hover:border-neutral-400'
                          }`}
                        >
                          <input
                            id={`election-${e.id}`}
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleElection(e.id)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="font-semibold">{e.name}</div>
                            <div className="text-sm text-neutral-600 mt-0.5">
                              {new Date(`${e.electionDay}T00:00:00`).toLocaleDateString('en-US', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                              {e.electionType ? ` · ${e.electionType}` : ''}
                              {e.jurisdictionType && e.jurisdictionType !== 'state'
                                ? ` · ${e.jurisdictionType}`
                                : ''}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                    {selectedElectionIds.length > 1 && (
                      <p className="text-xs text-neutral-500 mt-1">
                        Mailers are billed one election at a time — this order will cover the first
                        one you picked. Come back to set up the others after checkout.
                      </p>
                    )}
                  </div>
                )}

              {votingAddress &&
                !isLookingUp &&
                !isLoadingElections &&
                upcomingElections.length === 0 &&
                !electionsFailed && (
                  <p className="text-sm text-neutral-500">
                    No upcoming elections found
                    {resolvedState ? ` in ${resolvedState}` : ''}. Check back closer to election
                    season.
                  </p>
                )}

              {electionsFailed && (
                <Alert variant="destructive">
                  Couldn&apos;t load elections for your address. Try refreshing the page.
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Primary recipient */}
          <RecipientCard
            recipient={recipients[0]}
            index={0}
            onUpdate={(idx, rec) => dispatch({ type: 'UPDATE', index: idx, payload: rec })}
            onRemove={() => {}}
            showRemove={false}
          />

          {/* Additional (sponsored) recipients */}
          {recipients.slice(1).map((recipient, idx) => {
            const actualIdx = idx + 1;
            return (
              <RecipientCard
                key={`recipient-${recipient.recipientName}-${actualIdx}`}
                recipient={recipient}
                index={actualIdx}
                onUpdate={(index, rec) => dispatch({ type: 'UPDATE', index, payload: rec })}
                onRemove={(index) => dispatch({ type: 'REMOVE', index })}
                showRemove
              />
            );
          })}

          <Button type="button" variant="outline" onClick={() => dispatch({ type: 'ADD' })}>
            + Add a recipient
          </Button>
        </div>

        {/* Order summary sidebar */}
        <div>
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span>Mailers:</span>
                <span>{previewData?.mailerCount || 0}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span>Subtotal:</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>

              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <Label htmlFor="donation">Donation:</Label>
                  <Input
                    id="donation"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={donationAmount}
                    onChange={(e) => setDonationAmount(parseFloat(e.target.value) || 0)}
                    className="w-20 text-right text-sm"
                  />
                </div>
                <p className="text-xs text-neutral-500">
                  Round up: ${roundUpAmount.toFixed(2)} (optional)
                </p>
              </div>

              <div className="border-t border-neutral-200 pt-3 flex justify-between font-semibold">
                <span>Total:</span>
                <span>${suggestedTotal.toFixed(2)}</span>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-2">
              <Button
                type="submit"
                disabled={
                  isCreating || isRedirecting || isLookingUp || isLoadingElections || !electionId
                }
                className="w-full"
              >
                {isRedirecting && 'Redirecting to payment…'}
                {!isRedirecting && isCreating && 'Creating order…'}
                {!isCreating &&
                  !isRedirecting &&
                  (isLookingUp || isLoadingElections) &&
                  'Loading elections…'}
                {!isCreating &&
                  !isRedirecting &&
                  !isLookingUp &&
                  !isLoadingElections &&
                  'Review & Pay'}
              </Button>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
}
